import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { adminLogin } from '../../Redux/userSlice'
import admin from '../../public/images/telgrosadmin.svg'
import { useNavigate } from 'react-router-dom'
import Loader from '../../components/Loader/Loader'
import { 
  FiMail, 
  FiLock, 
  FiEye, 
  FiEyeOff, 
  FiLogIn, 
  FiRefreshCw,
  FiShield,
  FiCheckCircle
} from "react-icons/fi"
import { FaEyeSlash, FaEye } from 'react-icons/fa'

const Login = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { authSlice } = useSelector(state => state) || {}
  const { error, loading, user } = authSlice || {}

  const [credentials, setCredentials] = useState({ email: '', password: '' })
  const [formErrors, setFormErrors] = useState({ email: '', password: '' })
  const [isChecked, setIsChecked] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [adminData, setAdminData] = useState(false)
  const [isFocused, setIsFocused] = useState({ email: false, password: false })

  const handleChange = e => {
    const { name, value } = e.target
    setCredentials(prev => ({ ...prev, [name]: value }))
    setFormErrors(prev => ({ ...prev, [name]: '' }))
  }

  const validateForm = () => {
    const errors = {}
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    if (!credentials.email.trim()) {
      errors.email = 'Email is required.'
    } else if (!emailRegex.test(credentials.email)) {
      errors.email = 'Please enter a valid email address.'
    }

    if (!credentials.password.trim()) {
      errors.password = 'Password is required.'
    } else if (credentials.password.length < 6) {
      errors.password = 'Password must be at least 6 characters.'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = e => {
    e.preventDefault()
    if (!validateForm()) return
    dispatch(adminLogin({ ...credentials, rememberMe: isChecked ? 'yes' : 'no' }))
  }

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem('telgrossCredentials'))
    if (saved) {
      setCredentials(saved)
      setIsChecked(true)
    }

    const token = localStorage.getItem('accessToken')
    const adminId = localStorage.getItem('adminId')
    if (token && adminId) setIsChecked(true)
  }, [])

  useEffect(() => {
    try {
      const storedAdminData = localStorage.getItem("adminData");
      if (storedAdminData && typeof storedAdminData === "string") {
        setAdminData(JSON.parse(storedAdminData));
      }
    } catch (error) {
      console.error("Error parsing admin data from localStorage", error);
    }
  }, [user]);
  
  const multiSelectOptions = {
    'dashboard_analytics': "/app/dashboard",
    'user_management': "/app/users",
    'course_management': "/app/content",
    'sessiion_management': "/app/session",
    'community_management': "/app/community",
    'payement_moderation': "/app/payments&transactions",
    'Ads_management': "/app/adsAndPromotation",
    'certificate_management': "/app/certificate",
    'reward_management': "/app/referal/reward",
    'support_management': "/app/helpsupport",
    'notification_management': "/app/notifications",
    'preference_management': "/app/preferencepanagement",
    'admin_management': "/app/admin",
    'setting_management': "/app/appSetting",
  };
  
  useEffect(() => {
    if (user) {
      if (isChecked) {
        localStorage.setItem('telgrossCredentials', JSON.stringify(credentials))
      } else {
        localStorage.removeItem('telgrossCredentials')
      }
      if (adminData) {
        if (adminData?.access?.includes("dashboard_analytics")) {
          navigate('/app/dashboard');
        } else if (adminData?.isMain) {
          navigate('/app/dashboard');
        } else if (multiSelectOptions?.[adminData?.access?.[0]]) {
          navigate(multiSelectOptions[adminData?.access?.[0]]); 
        } else {
          navigate(multiSelectOptions[adminData?.access?.[0]]);
          navigate('/app/dashboard');
        }
      }
    }
  }, [user, navigate, adminData]);

  return (
    <div className='flex items-center justify-center min-h-screen bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] px-4 relative overflow-hidden'>
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#855DF6]/10 rounded-full blur-3xl -mr-48 -mt-48 animate-pulse"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#715EFE]/10 rounded-full blur-3xl -ml-48 -mb-48 animate-pulse"></div>
      <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-[#855DF6]/5 rounded-full blur-2xl -translate-x-1/2 -translate-y-1/2 animate-pulse"></div>
      
      <Loader loading={loading} />
      
      <div className='w-full max-w-xl p-6 bg-white/95 backdrop-blur-sm shadow-2xl rounded-2xl sm:p-10 relative z-10 border border-white/20'>
        {/* Logo */}
        <div className='flex flex-col items-center mb-6'>
          <div className='p-3 bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 rounded-2xl mb-4'>
            <img className='w-44 h-auto' src={admin} alt='Admin logo' />
          </div>
          
          <h2 className='text-3xl font-bold text-gray-800 mb-1'>
            Welcome Back
          </h2>
          <p className='text-center text-gray-500 text-sm'>
            Please enter your credentials to continue
          </p>
        </div>

        <form onSubmit={handleSubmit} className='space-y-5'>
          {/* Email */}
          <div>
            <label htmlFor='email' className='flex items-center gap-2 mb-2 text-sm font-medium text-gray-700'>
              <FiMail className='w-4 h-4 text-[#715EFE]' />
              Email Address
            </label>
            <div className={`relative transition-all duration-200 ${isFocused.email ? 'ring-2 ring-[#715EFE]/30 rounded-xl' : ''}`}>
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FiMail className={`w-5 h-5 ${formErrors.email ? 'text-red-400' : 'text-gray-400'}`} />
              </div>
              <input
                type='text'
                id='email'
                name='email'
                value={credentials.email}
                onChange={handleChange}
                onFocus={() => setIsFocused(prev => ({ ...prev, email: true }))}
                onBlur={() => setIsFocused(prev => ({ ...prev, email: false }))}
                placeholder='Enter your email'
                className={`w-full pl-10 pr-4 py-3 rounded-xl bg-gray-50 border transition-all duration-200 focus:outline-none
                  ${formErrors.email 
                    ? 'border-red-300 focus:ring-2 focus:ring-red-200' 
                    : 'border-gray-200 focus:border-[#715EFE]'
                  }`}
              />
            </div>
            {formErrors.email && (
              <p className='mt-1 text-sm text-red-500 flex items-center gap-1'>
                <span className="w-1 h-1 bg-red-500 rounded-full inline-block"></span>
                {formErrors.email}
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <label htmlFor='password' className='flex items-center gap-2 mb-2 text-sm font-medium text-gray-700'>
              <FiLock className='w-4 h-4 text-[#715EFE]' />
              Password
            </label>
            <div className={`relative transition-all duration-200 ${isFocused.password ? 'ring-2 ring-[#715EFE]/30 rounded-xl' : ''}`}>
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FiLock className={`w-5 h-5 ${formErrors.password ? 'text-red-400' : 'text-gray-400'}`} />
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                id='password'
                name='password'
                value={credentials.password}
                onChange={handleChange}
                onFocus={() => setIsFocused(prev => ({ ...prev, password: true }))}
                onBlur={() => setIsFocused(prev => ({ ...prev, password: false }))}
                placeholder='Enter your password'
                className={`w-full pl-10 pr-12 py-3 rounded-xl bg-gray-50 border transition-all duration-200 focus:outline-none
                  ${formErrors.password 
                    ? 'border-red-300 focus:ring-2 focus:ring-red-200' 
                    : 'border-gray-200 focus:border-[#715EFE]'
                  }`}
              />
              <button
                type='button'
                onClick={() => setShowPassword(prev => !prev)}
                className='absolute inset-y-0 right-3 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors duration-200'
              >
                {showPassword ? <FiEyeOff className='w-5 h-5' /> : <FiEye className='w-5 h-5' />}
              </button>
            </div>
            {formErrors.password && (
              <p className='mt-1 text-sm text-red-500 flex items-center gap-1'>
                <span className="w-1 h-1 bg-red-500 rounded-full inline-block"></span>
                {formErrors.password}
              </p>
            )}
          </div>

          {/* Remember Me */}
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2.5'>
              <input
                type='checkbox'
                id='remember'
                checked={isChecked}
                onChange={() => setIsChecked(!isChecked)}
                className='w-4 h-4 rounded border-gray-300 text-[#715EFE] focus:ring-[#715EFE] focus:ring-2 transition-all duration-200 cursor-pointer'
              />
              <label htmlFor='remember' className='text-sm text-gray-600 cursor-pointer'>
                Remember me
              </label>
            </div>
            <button
              type='button'
              className='text-sm text-[#715EFE] hover:text-[#855DF6] font-medium transition-colors duration-200'
            >
              Forgot Password?
            </button>
          </div>

          {/* Error message */}
          {error && (
            <div className='flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm'>
              <span className="w-1.5 h-1.5 bg-red-500 rounded-full flex-shrink-0"></span>
              {error}
            </div>
          )}

          {/* Submit Button */}
          <button
            type='submit'
            disabled={loading}
            className={`w-full py-3.5 font-semibold text-white rounded-xl bg-gradient-to-r from-[#855DF6] to-[#715EFE] 
              transition-all duration-300 hover:shadow-lg hover:shadow-[#715EFE]/30 
              disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3
              hover:scale-[1.01] active:scale-[0.98]`}
          >
            {loading ? (
              <>
                <FiRefreshCw className='w-5 h-5 animate-spin' />
                <span>Signing in...</span>
              </>
            ) : (
              <>
                <FiLogIn className='w-5 h-5' />
                <span>Sign In</span>
              </>
            )}
          </button>

          {/* Footer */}
          <p className='text-center text-xs text-gray-400 mt-4'>
            Secured with 🔒 256-bit encryption
          </p>
        </form>
      </div>
    </div>
  )
}

export default Login